import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  EmptyState,
  InfoBox,
  LoadingState,
  Toast,
  kstDotDateStr,
} from '@biznomi/ui-react'
import { SignaturePad, type SignaturePadHandle } from '@/components/SignaturePad'
import {
  fileSizeText,
  getMyContracts,
  openContractFile,
  signContract,
  type MyContract,
} from '@/lib/api/contract'

/**
 * 내 근로계약서 (직원).
 *
 * 회사가 올린 PDF 를 읽고 서명하는 화면. 우리가 계약 내용에 대해 하는 말은 없다 —
 * 무슨 조항인지, 유리한지, 법에 맞는지 판단하지 않는다 (§1 경계).
 *
 * 서명 버튼은 **한 번 열어본 뒤에만** 켜진다. 안 읽고 서명 눌리는 걸 막는 건
 * 판정이 아니라 순서다.
 */

function StatusBadge({ s }: { s: MyContract['status'] }) {
  if (s === 'SIGNED') return <Badge variant="info">서명 완료</Badge>
  if (s === 'VIEWED') return <Badge variant="warning">서명 대기</Badge>
  if (s === 'CANCELLED') return <Badge variant="outline">회수됨</Badge>
  return <Badge variant="warning">확인 필요</Badge>
}

export default function Contracts() {
  const qc = useQueryClient()
  const [toast, setToast] = useState('')
  const [error, setError] = useState('')
  const [opening, setOpening] = useState<string | null>(null)
  /** 이번 세션에 열어본 계약서 — 서명 버튼을 켜는 조건 */
  const [opened, setOpened] = useState<Set<string>>(new Set())
  const [signing, setSigning] = useState<MyContract | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['contracts', 'mine'],
    queryFn: getMyContracts,
  })
  const rows = data?.success ? data.data ?? [] : []

  async function view(c: MyContract) {
    setOpening(c.id)
    setError('')
    const res = await openContractFile(c.id)
    setOpening(null)

    if ('error' in res) {
      setError(res.error)
      return
    }

    // 새 탭에서 연다. iOS 는 iframe 안의 PDF 를 자주 못 그리는데 blob 탭은 뜬다.
    const win = window.open(res.url, '_blank')
    if (!win) {
      // 팝업이 막혔으면 같은 탭에서라도 보여준다 — 못 여는 것보다 낫다
      window.location.href = res.url
    }
    setOpened((s) => new Set(s).add(c.id))
    // 열람 시각이 서버에 기록됐으니 목록을 새로 받는다
    qc.invalidateQueries({ queryKey: ['contracts', 'mine'] })
    setTimeout(() => URL.revokeObjectURL(res.url), 60_000)
  }

  const pending = rows.filter((r) => r.status === 'PENDING' || r.status === 'VIEWED')

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-bold">근로계약서</h1>

      {pending.length > 0 && (
        <InfoBox tone="warning">
          아직 서명 안 한 계약서가 {pending.length}건 있습니다. 열어서 읽어 보고 서명해 주세요.
        </InfoBox>
      )}

      {error && (
        <p role="alert" className="rounded-lg bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}

      {isLoading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState
          title="받은 계약서가 없습니다"
          hint="회사에서 계약서를 올리면 여기에 뜹니다."
        />
      ) : (
        <ul className="space-y-3">
          {rows.map((c) => {
            const canSign = c.status !== 'SIGNED' && c.status !== 'CANCELLED'
            const readyToSign = canSign && (opened.has(c.id) || c.viewedAt !== null)
            return (
              <li key={c.id} className="space-y-3 rounded-card bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{c.title}</p>
                    <p className="mt-0.5 truncate text-xs text-slate-400">
                      {c.fileName} · {fileSizeText(c.fileSize)} · {kstDotDateStr(c.createdAt)}
                    </p>
                  </div>
                  <StatusBadge s={c.status} />
                </div>

                {c.signedAt && (
                  <p className="text-xs text-slate-500">
                    {kstDotDateStr(c.signedAt)} 서명함
                  </p>
                )}

                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    className="flex-1"
                    loading={opening === c.id}
                    onClick={() => void view(c)}
                  >
                    계약서 보기
                  </Button>
                  {canSign && (
                    <Button
                      className="flex-1"
                      disabled={!readyToSign}
                      onClick={() => setSigning(c)}
                    >
                      서명하기
                    </Button>
                  )}
                </div>

                {canSign && !readyToSign && (
                  <p className="text-xs text-slate-400">
                    먼저 계약서를 한 번 열어 보세요. 그래야 서명할 수 있습니다.
                  </p>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {signing && (
        <SignDialog
          contract={signing}
          onClose={() => setSigning(null)}
          onDone={(name) => {
            setSigning(null)
            qc.invalidateQueries({ queryKey: ['contracts', 'mine'] })
            qc.invalidateQueries({ queryKey: ['contracts', 'my-pending-count'] })
            setToast(`«${name}» 서명이 완료되었습니다`)
          }}
        />
      )}

      {toast && <Toast message={toast} variant="success" onClose={() => setToast('')} />}
    </div>
  )
}

function SignDialog({
  contract,
  onClose,
  onDone,
}: {
  contract: MyContract
  onClose: () => void
  onDone: (title: string) => void
}) {
  const padRef = useRef<SignaturePadHandle>(null)
  const [empty, setEmpty] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // 서명 도중 뒤로가기로 빠져나가도 서명이 남지 않게 정리
  useEffect(() => () => padRef.current?.clear(), [])

  async function submit() {
    const dataUrl = padRef.current?.toDataURL()
    if (!dataUrl) {
      setError('서명을 그려 주세요')
      return
    }
    setSaving(true)
    setError('')
    const res = await signContract(contract.id, dataUrl)
    setSaving(false)
    if (!res.success) {
      setError(res.error ?? '서명에 실패했습니다')
      return
    }
    onDone(contract.title)
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogTitle>서명</DialogTitle>

        <div className="space-y-4">
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <p className="truncate text-sm font-medium">{contract.title}</p>
            <p className="truncate text-xs text-slate-400">{contract.fileName}</p>
          </div>

          <SignaturePad ref={padRef} onEmptyChange={setEmpty} disabled={saving} />

          {/*
            우리가 남기는 것을 그대로 적는다. 서명의 법적 효력을 우리가 보증하지 않는다 —
            보증하는 건 "이 파일이 안 바뀌었다"와 "이 계정이 이 시각에 서명했다" 두 가지뿐이다.
          */}
          <p className="text-xs leading-relaxed text-slate-400">
            서명하면 서명한 그림과 시각이 남습니다. 나중에 이 계약서가 바뀌지 않았는지도
            확인할 수 있게 함께 기록해 둡니다.
          </p>

          {error && (
            <p role="alert" className="text-sm text-[var(--color-danger)]">{error}</p>
          )}

          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1" onClick={onClose} disabled={saving}>
              취소
            </Button>
            <Button className="flex-1" disabled={empty} loading={saving} onClick={() => void submit()}>
              서명 완료
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
